import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  GanttChart, 
  Users, 
  Clock, 
  Shield, 
  Trash2, 
  Search,
  Copy,
  RefreshCw,
  ArrowLeft,
  UserCheck,
  UserX,
  Loader2,
  Activity
} from 'lucide-react';
import ActivityLog from '../components/ActivityLog';

export default function AdminDashboard() {
  const { user } = useAuth();
  const [pendingSignups, setPendingSignups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  // Check if user is admin
  const isAdmin = user?.is_admin || user?.email?.toLowerCase() === 'agiprojects24@gmail.com';

  useEffect(() => {
    if (isAdmin) {
      loadData();
    }
  }, [isAdmin]);

  const loadData = async () => {
    try {
      const [signupsRes, usersRes] = await Promise.all([
        adminAPI.getPendingSignups(),
        adminAPI.getUsers()
      ]);
      setPendingSignups(signupsRes.data.signups || []);
      setUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const handleDeleteSignup = async (id) => {
    try {
      await adminAPI.deletePendingSignup(id);
      setPendingSignups(prev => prev.filter(s => s.id !== id));
      toast.success('Signup request deleted');
    } catch (error) {
      toast.error('Failed to delete signup request');
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      const res = await adminAPI.toggleUserAdmin(userId);
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, is_admin: res.data.is_admin } : u
      ));
      toast.success(`Admin status ${res.data.is_admin ? 'granted' : 'revoked'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await adminAPI.deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const copyOTP = (otp) => {
    navigator.clipboard.writeText(otp);
    toast.success('OTP copied to clipboard');
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900">
                <ArrowLeft className="w-4 h-4" />
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-9 h-9 bg-primary rounded-xl flex items-center justify-center">
                  <GanttChart className="w-5 h-5 text-white" />
                </div>
                <span className="font-outfit font-bold text-xl text-zinc-900">ProjectOS</span>
              </Link>
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                <Shield className="w-3 h-3 mr-1" />
                Admin
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">Admin Dashboard</h1>
          <p className="text-zinc-600">Manage users and signup requests</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-white border-zinc-200 rounded-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Pending Signups</p>
                <p className="text-2xl font-bold text-zinc-900">{pendingSignups.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-white border-zinc-200 rounded-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Users</p>
                <p className="text-2xl font-bold text-zinc-900">{users.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-white border-zinc-200 rounded-xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Admin Users</p>
                <p className="text-2xl font-bold text-zinc-900">{users.filter(u => u.is_admin).length}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList className="bg-zinc-100 p-1 rounded-xl">
            <TabsTrigger value="pending" className="rounded-lg data-[state=active]:bg-white">
              <Clock className="w-4 h-4 mr-2" />
              Pending Signups ({pendingSignups.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-white">
              <Users className="w-4 h-4 mr-2" />
              All Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="activity" className="rounded-lg data-[state=active]:bg-white">
              <Activity className="w-4 h-4 mr-2" />
              Activity Log
            </TabsTrigger>
          </TabsList>

          {/* Pending Signups Tab */}
          <TabsContent value="pending">
            <Card className="bg-white border-zinc-200 rounded-xl p-6">
              <h2 className="font-outfit font-semibold text-lg text-zinc-900 mb-4">
                Pending Signup Requests
              </h2>
              {pendingSignups.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-zinc-300" />
                  <p>No pending signup requests</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>OTP</TableHead>
                        <TableHead>Requested</TableHead>
                        <TableHead>Expires</TableHead>
                        <TableHead className="w-[100px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingSignups.map((signup) => (
                        <TableRow key={signup.id}>
                          <TableCell className="font-medium">{signup.name}</TableCell>
                          <TableCell>{signup.email}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="bg-zinc-100 px-2 py-1 rounded text-lg font-mono tracking-wider">
                                {signup.otp}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyOTP(signup.otp)}
                                title="Copy OTP"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-zinc-500 text-sm">
                            {formatDate(signup.created_at)}
                          </TableCell>
                          <TableCell className="text-zinc-500 text-sm">
                            {formatDate(signup.expires_at)}
                          </TableCell>
                          <TableCell>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Signup Request?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will reject the signup request for {signup.email}. 
                                    They will need to request access again.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction 
                                    onClick={() => handleDeleteSignup(signup.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* All Users Tab */}
          <TabsContent value="users">
            <Card className="bg-white border-zinc-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-outfit font-semibold text-lg text-zinc-900">
                  Registered Users
                </h2>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                  <Input
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-64 rounded-lg"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Auth</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="w-[150px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell className="font-medium">{u.name}</TableCell>
                        <TableCell>{u.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {u.auth_provider || 'email'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.is_admin ? (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                              <Shield className="w-3 h-3 mr-1" />
                              Admin
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-zinc-500">
                              Member
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-zinc-500 text-sm">
                          {formatDate(u.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {u.email.toLowerCase() !== 'agiprojects24@gmail.com' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleAdmin(u.id)}
                                  title={u.is_admin ? 'Remove admin' : 'Make admin'}
                                >
                                  {u.is_admin ? (
                                    <UserX className="w-4 h-4 text-orange-600" />
                                  ) : (
                                    <UserCheck className="w-4 h-4 text-green-600" />
                                  )}
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Delete User?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will permanently delete the user {u.email} and all their data.
                                        This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction 
                                        onClick={() => handleDeleteUser(u.id)}
                                        className="bg-red-600 hover:bg-red-700"
                                      >
                                        Delete
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </>
                            )}
                            {u.email.toLowerCase() === 'agiprojects24@gmail.com' && (
                              <Badge variant="outline" className="text-xs text-zinc-400">
                                Primary Admin
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activity">
            <ActivityLog showProjectName={true} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
